import { authHeaders, getAuthApiBaseUrl } from "./authApi";

async function parseJsonSafely(response) {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

function toReadableError(payload, fallbackMessage) {
  return payload?.message || payload?.error || fallbackMessage;
}

function apiBase() {
  return getAuthApiBaseUrl().replace(/\/+$/, "");
}

/**
 * Public wallet login: request SIWE-style message (nonce stored server-side).
 * @returns {Promise<{ message: string }>}
 */
export async function requestWalletLoginNonce(walletAddress) {
  const res = await fetch(`${apiBase()}/oauth/wallet/nonce`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ walletAddress }),
  });
  const data = await parseJsonSafely(res);
  if (!res.ok) {
    throw new Error(toReadableError(data, "Unable to start wallet sign-in."));
  }
  if (!data?.message) {
    throw new Error("Invalid response from server.");
  }
  return { message: data.message };
}

/**
 * Public wallet login: verify signature and receive session.
 * @returns {Promise<{ token: string, user: object }>}
 */
export async function verifyWalletLogin(walletAddress, signature) {
  const res = await fetch(`${apiBase()}/oauth/wallet/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ walletAddress, signature }),
  });
  const data = await parseJsonSafely(res);
  if (!res.ok) {
    throw new Error(toReadableError(data, "Wallet sign-in failed."));
  }
  if (!data?.token || !data?.user) {
    throw new Error("Wallet sign-in failed. Missing session data.");
  }
  return { token: data.token, user: data.user };
}

/**
 * Authenticated: request message to link wallet to the current account.
 * @returns {Promise<{ message: string } | { alreadyLinked: true, user: object }>}
 */
export async function requestWalletLinkNonce(walletAddress) {
  const res = await fetch(`${apiBase()}/oauth/wallet/link/nonce`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...authHeaders(),
    },
    body: JSON.stringify({ walletAddress }),
  });
  const data = await parseJsonSafely(res);
  if (!res.ok) {
    throw new Error(toReadableError(data, "Unable to start wallet link."));
  }
  if (data?.alreadyLinked && data?.user) {
    return { alreadyLinked: true, user: data.user };
  }
  if (!data?.message) {
    throw new Error("Invalid response from server.");
  }
  return { message: data.message };
}

/**
 * Authenticated: complete wallet link after signing `message`.
 * @returns {Promise<{ user: object }>}
 */
export async function linkWalletToAccount(walletAddress, signature) {
  const res = await fetch(`${apiBase()}/oauth/wallet/link`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...authHeaders(),
    },
    body: JSON.stringify({ walletAddress, signature }),
  });
  const data = await parseJsonSafely(res);
  if (!res.ok) {
    throw new Error(toReadableError(data, "Unable to link wallet."));
  }
  if (!data?.user) {
    throw new Error("Link succeeded but user payload was missing.");
  }
  return { user: data.user };
}
